import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { usePreviewService } from '../../../contexts/PreviewServiceContext';
import { ListingPreviewResponse } from '../../../system/preview';

/** Fetches the listing preview data and records the view */
export function usePreviewFetch() {
  const { listingKey } = useParams<{ listingKey: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || undefined;
  const { getListingPreview, setView } = usePreviewService();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<ListingPreviewResponse | null>(
    null,
  );
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    const fetchPreview = async () => {
      if (!listingKey || hasLoadedRef.current) return;

      setIsLoading(true);
      setError(null);

      try {
        const data = await getListingPreview(listingKey, token);
        hasLoadedRef.current = true;
        setPreviewData(data);
        setIsLoading(false);

        // Fire-and-forget: record the view
        if (token) {
          setView(listingKey, token).catch(() => {
            // Silently ignore — view tracking is best-effort
          });
        }
      } catch (err: any) {
        setError(
          err?.error || err?.message || 'Failed to load listing preview',
        );
        setIsLoading(false);
      }
    };

    fetchPreview();
  }, [listingKey, token, getListingPreview, setView]);

  return { isLoading, error, previewData };
}
